import { useMutation } from '@apollo/client';
import type { NotificationFrequency } from '@seek/ca-graphql-schema/types';
import { useMelwaysInfo } from '@seek/melways-react';
import { Box, RadioGroup, RadioItem } from 'braid-design-system';
import type { FunctionComponent } from 'react';

import { updatePreferenceQL } from 'src/modules/graphql/notifications/preference.graphql';
import { useTypedSelector } from 'src/reducers';
import type {
  NotificationProduct,
  UpdateNotificationSubscriptionMutation,
  UpdateNotificationSubscriptionMutationVariables,
} from 'src/types/graphql';

import { trackOptionSelectionPressed } from './tracking/tealium';

interface ProductRowToggleOptionsProps {
  product: NotificationProduct;
  frequency: NotificationFrequency;
  id: string;
  channelCode: string;
}

const ProductRowToggleOptions: FunctionComponent<
  ProductRowToggleOptionsProps
> = ({ product, frequency, channelCode, id }) => {
  const { language } = useMelwaysInfo();
  const locale = useTypedSelector(
    (state) => state.page.pageAnalytics.siteCountry,
  ).toUpperCase();

  const [updateFrequencySubscriptions] = useMutation<
    UpdateNotificationSubscriptionMutation,
    UpdateNotificationSubscriptionMutationVariables
  >(updatePreferenceQL);

  const updateCallback = ({
    currentTarget: { value },
  }: {
    currentTarget: { value: string };
  }) => {
    trackOptionSelectionPressed({
      product: product.code,
      channel: channelCode,
      optionSelectionType: 'radio',
      optionSelected: value,
    });

    updateFrequencySubscriptions({
      variables: {
        input: {
          productCode: product.code,
          channelCode,
          subscribe: true,
          frequency: value,
          locale: `${language}-${locale}`,
        },
      },
      optimisticResponse: buildOptimisticResponse({
        frequencyInput: value,
        key: id,
        frequency,
      }),
    });
  };

  return (
    <Box paddingTop="medium">
      <RadioGroup
        id="frequency"
        label=""
        value={frequency.value}
        onChange={updateCallback}
      >
        {frequency.options.map((option) => (
          <RadioItem
            key={option.code}
            label={option.label}
            value={option.code}
            description={option.description}
          />
        ))}
      </RadioGroup>
    </Box>
  );
};

function buildOptimisticResponse({
  frequencyInput,
  key,
  frequency,
}: {
  frequencyInput: string;
  key: string;
  frequency: NotificationFrequency;
}): UpdateNotificationSubscriptionMutation {
  return {
    updateNotificationSubscription: {
      __typename: 'NotificationPreference',
      subscriptions: [
        {
          id: key,
          __typename: 'NotificationSubscription',
          frequency: {
            __typename: 'NotificationFrequency',
            value: frequencyInput,
            options: frequency.options,
          },
          subscribed: true,
        },
      ],
    },
  };
}

export default ProductRowToggleOptions;
